import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Calendar,
  Camera,
  CheckCircle,
  FileText,
  Star,
  Trash2,
  User,
  XCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import EarningsReport from '../components/EarningsReport';
import TimeOffRequestModal from '../components/TimeOffRequestModal';
import ResourcePaystubsModal from '../components/ResourcePaystubsModal';
import ActionsRequiredPanel from '../components/admin-dashboard/ActionsRequiredPanel';
import { DashboardViewToggle } from '../components/admin-dashboard/DashboardViewToggle';

const bannerFont =
  "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif";

interface ResourceInfo {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  resourceId: string;
  avatarUrl?: string | null;
}

interface TimeOffRequest {
  id: string;
  start_date: string;
  end_date: string;
  request_type: string;
  is_partial_day: boolean;
  start_time?: string;
  end_time?: string;
  notes?: string;
  status: 'pending' | 'approved' | 'denied';
  requested_at: string;
}

function StarRow({ rating }: { rating: number }) {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  return (
    <span className="flex">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-3.5 w-3.5 ${
            star <= fullStars
              ? 'text-yellow-400 fill-current'
              : star === fullStars + 1 && hasHalfStar
                ? 'text-yellow-400 fill-current opacity-50'
                : 'text-white/25'
          }`}
        />
      ))}
    </span>
  );
}

function getStatusConfig(status: string) {
  switch (status) {
    case 'approved':
      return {
        icon: CheckCircle,
        label: 'Approved',
        textColor: 'text-green-600',
        bgColor: 'bg-green-50',
      };
    case 'denied':
      return { icon: XCircle, label: 'Denied', textColor: 'text-red-500', bgColor: 'bg-red-50' };
    default:
      return {
        icon: AlertCircle,
        label: 'Pending',
        textColor: 'text-orange-500',
        bgColor: 'bg-orange-50',
      };
  }
}

const TYPE_LABELS: Record<string, string> = {
  vacation: 'Vacation',
  sick: 'Sick Leave',
  personal: 'Personal Day',
  family: 'Family Emergency',
  bereavement: 'Bereavement',
  other: 'Other',
};

function formatRequestDate(dateStr: string) {
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    return format(new Date(year, month - 1, day), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
}

/**
 * Resource home at /admin-dashboard — the staff version of the Control Centre.
 * Same banner language as the admin dashboard, containing everything from the
 * old profile page: avatar, rating, earnings, paystubs, and time off.
 */
export default function ResourceControlCentre() {
  const { selectedBusiness } = useBusiness();
  const [info, setInfo] = useState<ResourceInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [rating, setRating] = useState<{ rating: number; feedbackCount: number } | null>(null);
  const [isTimeOffModalOpen, setIsTimeOffModalOpen] = useState(false);
  const [isPaystubsModalOpen, setIsPaystubsModalOpen] = useState(false);
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
  const [isLoadingTimeOff, setIsLoadingTimeOff] = useState(false);
  const [timeOffDisplayLimit, setTimeOffDisplayLimit] = useState(5);
  const [now, setNow] = useState(() => new Date());

  const timezone = selectedBusiness?.timezone || 'America/Los_Angeles';

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(tick);
  }, []);

  const dateTimeDisplay = useMemo(() => {
    const time = formatInTimeZone(now, timezone, 'h:mm aaa');
    const dateShort = formatInTimeZone(now, timezone, 'EEEE, MMM d');
    const desktopLine = `${formatInTimeZone(now, timezone, 'EEEE, MMMM do, yyyy')}, ${time}`;
    return { time, dateShort, desktopLine };
  }, [now, timezone]);

  useEffect(() => {
    const loadInfo = async () => {
      try {
        if (!supabase) return;
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) return;

        const { data: resourceData, error } = await supabase
          .from('resources')
          .select('*, role:resource_roles(id, name, description)')
          .eq('auth_user_id', session.user.id)
          .single();

        if (error) {
          console.error('Error getting resource:', error);
          return;
        }
        if (resourceData) {
          setInfo({
            firstName: resourceData.first_name || '',
            lastName: resourceData.last_name || '',
            email: resourceData.email || '',
            role: resourceData.role?.name || resourceData.role || 'Resource',
            resourceId: resourceData.id,
            avatarUrl: resourceData.avatar_url,
          });
        }
      } catch (err) {
        console.error('Error loading resource info:', err);
      } finally {
        setLoadingInfo(false);
      }
    };
    void loadInfo();
  }, []);

  const loadRating = useCallback(async () => {
    if (!info?.resourceId || !selectedBusiness?.id || !supabase) return;
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: feedbackData, error } = await supabase
        .from('booking_feedback')
        .select('*, bookings!inner(id, business_id, booking_resources!inner(resource_id))')
        .eq('business_id', selectedBusiness.id)
        .eq('bookings.booking_resources.resource_id', info.resourceId)
        .eq('status', 'submitted')
        .gte('submitted_at', thirtyDaysAgo.toISOString())
        .lte('submitted_at', new Date().toISOString());

      if (error) {
        console.error('Error fetching resource rating:', error);
        return;
      }
      if (!feedbackData || feedbackData.length === 0) {
        setRating({ rating: 0, feedbackCount: 0 });
        return;
      }

      let totalRating = 0;
      let totalRatingCount = 0;
      feedbackData.forEach((feedback) => {
        let feedbackAverage = 0;
        let ratingCount = 0;
        if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
          Object.values(feedback.form_responses).forEach((value) => {
            if (typeof value === 'number' && value > 0) {
              feedbackAverage += value;
              ratingCount++;
            }
          });
        } else {
          if (feedback.overall_quality_rating) {
            feedbackAverage += feedback.overall_quality_rating;
            ratingCount++;
          }
          if (feedback.overall_experience_rating) {
            feedbackAverage += feedback.overall_experience_rating;
            ratingCount++;
          }
        }
        if (ratingCount > 0) {
          totalRating += feedbackAverage / ratingCount;
          totalRatingCount++;
        }
      });

      setRating({
        rating: totalRatingCount > 0 ? totalRating / totalRatingCount : 0,
        feedbackCount: feedbackData.length,
      });
    } catch (err) {
      console.error('Error loading resource rating:', err);
    }
  }, [info?.resourceId, selectedBusiness?.id]);

  const loadTimeOffRequests = useCallback(async () => {
    if (!info?.resourceId || !selectedBusiness?.id || !supabase) return;
    setIsLoadingTimeOff(true);
    try {
      const { data: requests, error } = await supabase
        .from('time_off_requests')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('resource_id', info.resourceId)
        .order('requested_at', { ascending: false })
        .limit(10);
      if (error) {
        console.error('Error fetching time off requests:', error);
        return;
      }
      setTimeOffRequests(requests || []);
    } catch (err) {
      console.error('Error loading time off requests:', err);
    } finally {
      setIsLoadingTimeOff(false);
    }
  }, [info?.resourceId, selectedBusiness?.id]);

  useEffect(() => {
    if (!info?.resourceId || !selectedBusiness?.id || !supabase) return;
    void loadRating();
    void loadTimeOffRequests();

    const timeOffSubscription = supabase
      .channel('resource_cc_time_off_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'time_off_requests',
          filter: `resource_id=eq.${info.resourceId}`,
        },
        () => void loadTimeOffRequests(),
      )
      .subscribe();

    return () => {
      supabase?.removeChannel(timeOffSubscription);
    };
  }, [info?.resourceId, selectedBusiness?.id, loadRating, loadTimeOffRequests]);

  const handleCancelRequest = async (requestId: string) => {
    if (!supabase || !info?.resourceId) return;
    try {
      const { error } = await supabase
        .from('time_off_requests')
        .delete()
        .eq('id', requestId)
        .eq('resource_id', info.resourceId);
      if (error) {
        console.error('Error canceling time off request:', error);
        return;
      }
      void loadTimeOffRequests();
    } catch (err) {
      console.error('Error canceling time off request:', err);
    }
  };

  const handleProfilePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !supabase || !info?.resourceId) return;
    try {
      setIsUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${info.resourceId}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });
      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from('avatars').getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('resources')
        .update({ avatar_url: publicUrl })
        .eq('id', info.resourceId);
      if (updateError) throw updateError;

      setInfo((prev) => (prev ? { ...prev, avatarUrl: publicUrl } : prev));
    } catch (err) {
      console.error('Error uploading profile photo:', err);
    } finally {
      setIsUploading(false);
    }
  };

  if (loadingInfo) {
    return (
      <div className="control-centre pt-3 pb-8 px-3 md:pt-4 md:px-4 w-full max-w-none">
        <div className="flex items-center justify-center min-h-[min(480px,70vh)]">
          <div className="w-8 h-8 border-[3px] border-gray-300 border-t-[#007AFF] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="control-centre pt-3 pb-8 px-3 md:pt-4 md:px-4 w-full max-w-none">
        <div className="flex flex-col items-center justify-center text-center min-h-[min(480px,70vh)]">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-50 border border-gray-100 text-gray-400 mb-4">
            <User className="h-7 w-7" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900">Control Centre</h1>
          <p className="text-sm text-gray-500 mt-2 max-w-sm">
            We couldn't find your team profile. Ask your administrator to link your account.
          </p>
        </div>
      </div>
    );
  }

  const displayName = `${info.firstName} ${info.lastName}`.trim() || 'Team member';
  const headerButton =
    'shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 active:scale-[0.98] touch-manipulation';

  return (
    <div className="control-centre pt-3 pb-8 px-3 md:pt-4 md:px-4 w-full max-w-none" style={{ fontFamily: bannerFont }}>
      <section className="relative pt-3 sm:pt-4 mb-4 lg:mb-6" aria-label="Welcome banner">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <label className="relative cursor-pointer group shrink-0">
              <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm sm:h-12 sm:w-12">
                {info.avatarUrl ? (
                  <img src={info.avatarUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <User className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/45 opacity-0 transition-opacity group-hover:opacity-100">
                <Camera className="h-4 w-4 text-white" />
              </div>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleProfilePhotoUpload}
                disabled={isUploading}
              />
              {isUploading ? (
                <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/45">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                </div>
              ) : null}
            </label>
            <div className="min-w-0">
              <h1
                className="truncate text-[24px] font-bold leading-[1.15] text-gray-900 sm:text-[28px]"
                style={{ letterSpacing: '-0.02em' }}
              >
                {displayName}
              </h1>
              <p className="mt-0.5 truncate text-[12px] font-medium leading-tight text-gray-700 sm:text-[13px]">
                {info.firstName ? `Welcome back, ${info.firstName}` : 'Welcome back'}
              </p>
              <p className="text-[12px] leading-tight text-gray-500 sm:text-[13px]">{dateTimeDisplay.desktopLine}</p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2" aria-label="Quick actions">
            <DashboardViewToggle />
            <button type="button" onClick={() => setIsTimeOffModalOpen(true)} className={headerButton} style={{ WebkitTapHighlightColor: 'transparent' }}>
              <Calendar className="h-4 w-4 text-gray-500" aria-hidden />
              Time off
            </button>
            <button type="button" onClick={() => setIsPaystubsModalOpen(true)} className={headerButton} style={{ WebkitTapHighlightColor: 'transparent' }}>
              <FileText className="h-4 w-4 text-gray-500" aria-hidden />
              Paystubs
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-gray-200 pb-3">
          <p className="flex items-baseline gap-2 text-[13px] text-gray-500">
            <span>Role</span>
            <span className="font-semibold text-gray-900">{info.role}</span>
          </p>
          {info.email ? (
            <>
              <span className="hidden h-4 w-px bg-gray-200 sm:block" aria-hidden />
              <p className="min-w-0 truncate text-[13px] text-gray-500">{info.email}</p>
            </>
          ) : null}
          {rating && rating.feedbackCount > 0 ? (
            <>
              <span className="hidden h-4 w-px bg-gray-200 sm:block" aria-hidden />
              <p className="flex items-center gap-2 text-[13px] text-gray-500">
                <span>Rating</span>
                <StarRow rating={rating.rating} />
                <span className="font-semibold tabular-nums text-gray-900">{rating.rating.toFixed(1)}</span>
                <span className="text-[12px] text-gray-400">
                  {rating.feedbackCount} review{rating.feedbackCount === 1 ? '' : 's'}
                </span>
              </p>
            </>
          ) : null}
        </div>
      </section>

      <div className="flex flex-col gap-4">
        <EarningsReport
          userInfo={{
            resourceId: info.resourceId,
            firstName: info.firstName,
            lastName: info.lastName,
            email: info.email,
            avatarUrl: info.avatarUrl || undefined,
          }}
        />
        <ActionsRequiredPanel variant="hero" />

        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm" aria-label="Time off requests">
          <h2 className="border-b border-gray-100 px-4 py-3 text-[13px] font-medium text-gray-500 sm:px-5">Time off requests</h2>
            {isLoadingTimeOff ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-6 h-6 border-[3px] border-gray-200 border-t-[#007AFF] rounded-full animate-spin" />
                <span className="ml-3 text-[15px] text-gray-500">Loading requests...</span>
              </div>
            ) : timeOffRequests.length > 0 ? (
              <div>
                {timeOffRequests.slice(0, timeOffDisplayLimit).map((request, index) => {
                  const config = getStatusConfig(request.status);
                  const StatusIcon = config.icon;
                  return (
                    <div key={request.id}>
                      {index > 0 && <div className="h-px bg-gray-200 ml-4" />}
                      <div className="px-4 py-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0 mr-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[16px] font-medium text-black">
                                {TYPE_LABELS[request.request_type] || request.request_type}
                              </span>
                              <span
                                className={`text-[12px] font-semibold px-2 py-0.5 rounded-full ${config.textColor} ${config.bgColor}`}
                              >
                                {config.label}
                              </span>
                            </div>
                            <p className="text-[14px] text-gray-500">
                              {formatRequestDate(request.start_date)}
                              {request.start_date !== request.end_date &&
                                ` - ${formatRequestDate(request.end_date)}`}
                            </p>
                            {request.is_partial_day && request.start_time && request.end_time && (
                              <p className="text-[13px] text-gray-400 mt-0.5">
                                {request.start_time} - {request.end_time}
                              </p>
                            )}
                            {request.notes && (
                              <p className="text-[13px] text-gray-400 mt-1 line-clamp-2">
                                {request.notes}
                              </p>
                            )}
                            <p className="text-[12px] text-gray-400 mt-1.5">
                              Requested {format(new Date(request.requested_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
                            <StatusIcon className={`h-5 w-5 ${config.textColor}`} />
                            {(request.status === 'pending' || request.status === 'approved') && (
                              <button
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      'Are you sure you want to cancel this time off request?',
                                    )
                                  ) {
                                    void handleCancelRequest(request.id);
                                  }
                                }}
                                className="p-1.5 text-red-400 hover:text-red-600 active:bg-red-50 rounded-lg transition-colors"
                                title="Cancel request"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {timeOffRequests.length > timeOffDisplayLimit && (
                  <>
                    <div className="h-px bg-gray-200" />
                    <button
                      onClick={() => setTimeOffDisplayLimit((prev) => prev + 5)}
                      className="w-full py-3 text-center text-[15px] font-medium text-[#007AFF] active:bg-gray-100 transition-colors"
                    >
                      Show More ({timeOffRequests.length - timeOffDisplayLimit} remaining)
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="px-6 py-10 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <Calendar className="h-6 w-6 text-gray-400" />
                </div>
                <h4 className="mb-1 text-[16px] font-semibold text-gray-900">No requests</h4>
                <p className="mx-auto max-w-[280px] text-[14px] text-gray-500">
                  Tap Time off above to submit your first request.
                </p>
              </div>
            )}
        </section>
      </div>

      {/* Modals */}
      <TimeOffRequestModal
        isOpen={isTimeOffModalOpen}
        onClose={() => setIsTimeOffModalOpen(false)}
        onSuccess={() => {
          setIsTimeOffModalOpen(false);
          void loadTimeOffRequests();
        }}
        resourceId={info.resourceId}
      />

      {selectedBusiness?.id && (
        <ResourcePaystubsModal
          isOpen={isPaystubsModalOpen}
          onClose={() => setIsPaystubsModalOpen(false)}
          resourceId={info.resourceId}
          businessId={selectedBusiness.id}
          resourceInfo={{
            name: displayName,
            email: info.email,
            avatarUrl: info.avatarUrl || undefined,
          }}
        />
      )}
    </div>
  );
}
